import { useState, useEffect, useContext, createContext, ReactNode, useCallback } from 'react';
import { apiService, User } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ requiresTwoFactor?: boolean }>;
  verifyTwoFactor: (email: string, password: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      await apiService.logout();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const isAuth = await apiService.isAuthenticated();
      if (isAuth) {
        await refreshUser();
      } else {
        setLoading(false);
      }
    };
    checkAuth();
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    const result = await apiService.login({ email, password });

    if (result.requiresTwoFactor) {
      return { requiresTwoFactor: true };
    }

    if (result.user) {
      setUser(result.user);
    }
    return {};
  };

  const verifyTwoFactor = async (email: string, password: string, token: string) => {
    const result = await apiService.verifyTwoFactor({ email, password, token });
    if (result.user) {
      setUser(result.user);
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      verifyTwoFactor,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}